import React, {useState} from 'react';
import {Box, Button, IconButton} from '@mui/material';
import {Add as AddIcon, Close as CloseIcon} from '@mui/icons-material';
import {useSnackbar} from 'notistack';
import LeadContactDialog from './LeadContactDialog';
import LeadDetailDialog from './LeadDetailDialog';
import {LeadCrudService} from '../../api/generated/requests/services.gen';
import {LeadDTO, AttributeDTO} from "../../api";
import {useAttributesServicePostApiAttributesBulk} from '../../api/generated/queries/queries';

interface NewLeadButtonProps {
  refreshLeads?: () => Promise<any>;
}

const NewLeadButton: React.FC<NewLeadButtonProps> = ({ refreshLeads }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openLeadDetailDialog, setOpenLeadDetailDialog] = useState(false);
  const [createdLeadId, setCreatedLeadId] = useState<number | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  
  // Mutation hook for attribute operations
  const postAttributesBulk = useAttributesServicePostApiAttributesBulk();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handler functions for LeadDetailDialog
  const handleCloseLeadDetailDialog = () => {
    setOpenLeadDetailDialog(false);
    setCreatedLeadId(null);
  };

  const handleLeadUpdated = async () => {
    // Odśwież listę leadów, jeśli funkcja została przekazana
    if (refreshLeads) {
      await refreshLeads();
    }
    // Wyślij globalne zdarzenie do odświeżenia listy leadów
    window.dispatchEvent(new CustomEvent('refresh-leads'));
  };

  const handleSaveLead = async (lead: LeadDTO, createNext?: boolean, attributes?: AttributeDTO[]): Promise<LeadDTO | void> => {
    setIsSubmitting(true);
    try {
      // Step 1: Create the contact
      const response = await LeadCrudService.postApiLeadContacts({
        requestBody: lead
      });
      setCreatedLeadId(response.id || null);

      // Step 2: Save attributes if provided and contact was created successfully
      if (response.id && attributes && attributes.length > 0) {
        // Update attributes with the actual leadId
        const attributesWithLeadId = attributes.map(attr => ({
          ...attr,
          relatedObjectId: response.id
        }));
        
        await postAttributesBulk.mutateAsync({
          requestBody: attributesWithLeadId
        });
      }

      // Step 3: Show success message (contact and attributes are now saved)
      enqueueSnackbar(`Kontakt został utworzony (ID: ${response.prefixId}-${response.id})`, {
        variant: 'success',
        autoHideDuration: 15000,
        action: (snackbarId) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                if (response.id) {
                  setOpenLeadDetailDialog(true);
                }
              }}
            >
              Otwórz
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )
      });

      if (!createNext) {
        // If "Create Next" is not checked, close dialog
        handleCloseDialog();
      }
      // If "Create Next" is checked, keep dialog open (form will be reset by Formik)

      // Odśwież listę leadów, jeśli funkcja została przekazana
      if (refreshLeads) {
        await refreshLeads();
      }

      // Wyślij globalne zdarzenie do odświeżenia listy leadów
      window.dispatchEvent(new CustomEvent('refresh-leads'));

      // Return the created lead for task confirmation dialog
      return response;
    } catch (error) {
      console.error('Błąd podczas tworzenia leada:', error);
      enqueueSnackbar('Wystąpił błąd podczas tworzenia leada', { variant: 'error' });
      throw error; // Re-throw to let LeadContactDialog handle it
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        data-testid="new-lead-button"
        onClick={handleOpenDialog}
        startIcon={<AddIcon />}
        sx={{
          background: 'linear-gradient(to right, #06b6d4, #2563eb)',
          color: 'white',
          px: 2,
          py: 0.5,
          borderRadius: '9999px', // pełna pastylka
          fontWeight: 400,
          textTransform: 'none',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
          },
        }}
      >
        Nowy kontakt
      </Button>

      <LeadContactDialog
        open={openDialog}
        onClose={handleCloseDialog}
        lead={null}
        onSave={handleSaveLead}
        isSubmitting={isSubmitting}
      />

      <LeadDetailDialog
        open={openLeadDetailDialog}
        leadId={createdLeadId}
        onClose={handleCloseLeadDetailDialog}
        onLeadUpdated={handleLeadUpdated}
      />
    </>
  );
};

export default NewLeadButton;
